import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, Loader2, ExternalLink, Calendar, Tag, Sparkles } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { EXTERNAL_LINKS } from '../../constants/links';
import { PromoModal } from './PromoModal';
import skylarqLogo from '../../assets/skylarq_logo_small.png';

interface AiSummary {
  title: string;
  overview: string;
  sections: { title: string; items: string[] }[];
}

interface SharedMeetingData {
  shared_meeting: {
    share_token: string;
    title: string | null;
    access_count: number;
    max_access_count: number | null;
    expires_at: string | null;
  };
  meeting: {
    title: string | null;
    category: string | null;
    created_at: string;
  };
  ai_summary: AiSummary | null;
  sharer_name: string | null;
}

interface ErrorState {
  type: 'not_found' | 'expired' | 'limit_exceeded' | 'network_error';
  message: string;
}

const DOWNLOAD_URL = EXTERNAL_LINKS.DESKTOP_DOWNLOAD;

export const PublicMeetingView: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [data, setData] = useState<SharedMeetingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ErrorState | null>(null);
  const [showPromo, setShowPromo] = useState(false);

  useEffect(() => {
    const loadSharedMeeting = async () => {
      if (!shareToken) {
        setError({ type: 'not_found', message: 'Invalid share link' });
        setLoading(false);
        return;
      }

      try {
        setError(null);
        setLoading(true);
        const result = await apiClient.getSharedMeeting(shareToken);

        if (!result) {
          setError({
            type: 'not_found',
            message: 'This shared meeting could not be found, has expired, or has reached its view limit.',
          });
        } else {
          setData(result);

          // Show promo modal on first visit
          if (!localStorage.getItem('skylarq_meeting_promo_dismissed') && result.sharer_name) {
            setShowPromo(true);
          }
        }
      } catch (err: unknown) {
        const typed = err as { type?: string; message?: string };
        if (typed?.type === 'expired') {
          setError({ type: 'expired', message: 'This shared meeting has expired.' });
        } else if (typed?.type === 'limit_exceeded') {
          setError({ type: 'limit_exceeded', message: 'This shared meeting has reached its view limit.' });
        } else {
          setError({ type: 'network_error', message: 'Failed to load the shared meeting. Please try again later.' });
        }
      } finally {
        setLoading(false);
      }
    };

    loadSharedMeeting();
  }, [shareToken]);

  const handleDismissPromo = () => {
    setShowPromo(false);
    localStorage.setItem('skylarq_meeting_promo_dismissed', 'true');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (error || (!loading && !data)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center bg-white rounded-2xl p-8 shadow-sm">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h1 className="text-lg font-semibold text-gray-900 mb-2">
            {error?.type === 'expired' ? 'Meeting Link Expired' :
             error?.type === 'limit_exceeded' ? 'View Limit Reached' : 'Meeting Not Found'}
          </h1>
          <p className="text-sm text-gray-600 mb-6">{error?.message || 'This shared meeting is no longer available.'}</p>
          <a
            href={DOWNLOAD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Go to Skylarq
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading meeting notes...</p>
        </div>
      </div>
    );
  }

  const { meeting, ai_summary, shared_meeting, sharer_name } = data!;
  const displayTitle = ai_summary?.title || shared_meeting.title || meeting.title || 'Shared Meeting';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {showPromo && sharer_name && (
        <PromoModal sharerName={sharer_name} onDismiss={handleDismissPromo} />
      )}

      <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <a
            href={DOWNLOAD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 transition-opacity hover:opacity-70"
          >
            <img src={skylarqLogo} alt="Skylarq" className="w-7 h-7 object-contain" />
            <span className="text-base font-semibold text-gray-900 hidden sm:inline">Skylarq</span>
          </a>

          <div className="flex items-center gap-2">
            <a
              href={DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
            >
              Open in Skylarq
            </a>
            <a
              href={DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              Sign in
            </a>
          </div>
        </div>
      </header>

      {(shared_meeting.expires_at || shared_meeting.max_access_count) && (
        <div className="bg-amber-50 border-b border-amber-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-2">
            <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-800">
              {shared_meeting.expires_at && `Expires ${formatDate(shared_meeting.expires_at)}. `}
              {shared_meeting.max_access_count && `${Math.max(0, shared_meeting.max_access_count - shared_meeting.access_count)} views remaining.`}
            </p>
          </div>
        </div>
      )}

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">{displayTitle}</h1>

        <div className="flex flex-wrap items-center gap-2 mb-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
            <Calendar className="w-3.5 h-3.5" />
            {formatDate(meeting.created_at)}
          </span>
          {meeting.category && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
              <Tag className="w-3.5 h-3.5" />
              {meeting.category}
            </span>
          )}
        </div>

        {ai_summary ? (
          <div className="space-y-6">
            {ai_summary.overview && (
              <p className="text-base text-gray-700 leading-relaxed">{ai_summary.overview}</p>
            )}

            {ai_summary.sections?.map((section, i) => (
              <div key={i}>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  {section.title}
                </h2>
                <ul className="space-y-2 pl-5 list-disc marker:text-gray-400">
                  {section.items?.map((item, j) => (
                    <li key={j} className="text-base text-gray-800 leading-relaxed pl-1">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-sm text-gray-500">No notes available for this meeting.</p>
          </div>
        )}
      </main>

      <footer className="sticky bottom-0 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-gray-600" />
            <p className="text-sm text-gray-700">
              <span className="font-medium">Build your own meeting notes</span>
              <span className="text-gray-500 ml-1.5 hidden sm:inline">with AI-powered transcription</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors hidden sm:block"
            >
              Sign in
            </a>
            <a
              href={DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-4 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-medium hover:bg-gray-800 transition-colors"
            >
              Get Started Free
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
